<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  totalContacts: {
    type: Number,
    default: 0,
  },
  processedContacts: {
    type: Number,
    default: 0,
  },
  failedContacts: {
    type: Number,
    default: 0,
  },
  deliveredContacts: {
    type: Number,
    default: 0,
  },
  readContacts: {
    type: Number,
    default: 0,
  },
  repliedContacts: {
    type: Number,
    default: 0,
  },
  isProcessing: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['viewErrors']);

const { t } = useI18n();

const progressPercentage = computed(() => {
  if (props.totalContacts === 0) return 0;
  return Math.round((props.processedContacts / props.totalContacts) * 100);
});

const sentContacts = computed(() => {
  return Math.max(props.processedContacts - props.failedContacts, 0);
});

const progressDescription = computed(() => {
  return t('CAMPAIGN.WHATSAPP.CARD.PROGRESS.PROCESSING_STATUS', {
    processed: props.processedContacts,
    total: props.totalContacts,
  });
});

const funnelRate = value => {
  if (props.totalContacts === 0) return '0%';
  return `${Math.round((value / props.totalContacts) * 100)}%`;
};
</script>

<template>
  <div class="w-full">
    <!-- Progress bar during processing -->
    <div v-if="isProcessing" class="flex flex-col gap-1">
      <div
        class="flex items-center w-full h-2 overflow-hidden rounded-full bg-n-alpha-2"
      >
        <div
          class="h-full transition-all duration-300 rounded-full bg-n-brand"
          :style="{ width: `${progressPercentage}%` }"
        />
      </div>
      <div class="flex items-center gap-1 text-xs text-n-slate-11">
        <span class="font-medium">{{ progressPercentage }}%</span>
        <span class="mx-1">{{ progressDescription }}</span>
      </div>
    </div>

    <!-- Funnel summary after completion -->
    <div v-else class="flex flex-wrap items-center gap-3 text-xs">
      <div
        v-if="sentContacts > 0"
        class="flex items-center gap-1 text-n-teal-11"
      >
        <span class="i-lucide-send size-3.5" />
        <span class="font-medium">{{ sentContacts }}</span>
        <span>{{ t('CAMPAIGN.WHATSAPP.CARD.PROGRESS.SENT') }}</span>
        <span class="text-n-slate-10">({{ funnelRate(sentContacts) }})</span>
      </div>
      <div
        v-if="deliveredContacts > 0"
        class="flex items-center gap-1 text-n-blue-11"
      >
        <span class="i-lucide-check-check size-3.5" />
        <span class="font-medium">{{ deliveredContacts }}</span>
        <span>{{ t('CAMPAIGN.WHATSAPP.CARD.PROGRESS.DELIVERED') }}</span>
        <span class="text-n-slate-10"
          >({{ funnelRate(deliveredContacts) }})</span
        >
      </div>
      <div
        v-if="readContacts > 0"
        class="flex items-center gap-1 text-n-violet-11"
      >
        <span class="i-lucide-eye size-3.5" />
        <span class="font-medium">{{ readContacts }}</span>
        <span>{{ t('CAMPAIGN.WHATSAPP.CARD.PROGRESS.READ') }}</span>
        <span class="text-n-slate-10">({{ funnelRate(readContacts) }})</span>
      </div>
      <div
        v-if="repliedContacts > 0"
        class="flex items-center gap-1 text-n-green-11"
      >
        <span class="i-lucide-reply size-3.5" />
        <span class="font-medium">{{ repliedContacts }}</span>
        <span>{{ t('CAMPAIGN.WHATSAPP.CARD.PROGRESS.REPLIED') }}</span>
        <span class="text-n-slate-10">({{ funnelRate(repliedContacts) }})</span>
      </div>
      <button
        v-if="failedContacts > 0"
        type="button"
        class="flex items-center gap-1 text-n-ruby-11 hover:text-n-ruby-12 cursor-pointer transition-colors"
        :title="t('CAMPAIGN.WHATSAPP.CARD.PROGRESS.VIEW_ERRORS')"
        @click.stop="emit('viewErrors')"
      >
        <span class="i-lucide-x size-3.5" />
        <span class="font-medium">{{ failedContacts }}</span>
        <span class="underline">{{
          t('CAMPAIGN.WHATSAPP.CARD.PROGRESS.FAILED')
        }}</span>
      </button>
      <div class="flex items-center gap-1 text-n-slate-11">
        <span class="i-lucide-users size-3.5" />
        <span class="font-medium">{{ totalContacts }}</span>
        <span>{{ t('CAMPAIGN.WHATSAPP.CARD.PROGRESS.TOTAL') }}</span>
      </div>
    </div>
  </div>
</template>
